import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Stack,
  Tag,
  TagLabel,
  Text,
  VStack,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaWarehouse } from "react-icons/fa";
import ReactS3Uploader from "react-s3-uploader";
import VenueDisplayHeader from "../../VenueDisplayHeader";
import {
  FiCalendar,
  FiCopy,
  FiEdit2,
  FiExternalLink,
  FiInfo,
} from "react-icons/fi";
import { EventDetailSummarySection } from "@common/interfaces/KYDEvent";
import { useLoading, useProgress } from "../../hooks/form";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import actions from "../../../actions";
import { useDialog } from "../../../Common/Dialog";
import { EditModalType, showSuccessToast } from "../../../utils";
import EditVenueModal from "../Venue/EditVenueModal";
import { SectionProps } from "../sections/sectionsComponents";
import EditNameModal from "./EditNameModal";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import { IoEllipsisVertical } from "react-icons/io5";
import DuplicateEventModal from "./DuplicateEventModal";
import { useNavigate } from "react-router-dom";
import { ScheduledUpdatesBadge } from "../Lineup/ScheduledUpdatesBadge";
import useScheduledUpdates from "../ScheduledUpdates/hooks/useScheduledUpdates";
import {
  ScheduledUpdatesContext,
  withScheduledUpdates,
} from "../ScheduledUpdates/ScheduledUpdatesContext";
import UpcomingScheduledUpdateDialog, {
  UpcomingScheduledUpdateDialogContext,
  withUpcomingScheduledUpdateDialog,
} from "../ScheduledUpdates/UpcomingScheduledUpdateDialog";
import EventDetailTitle from "../../EventDetailTitle";
import KYDMultiSelect, { Option } from "../../../ui/KYDMultiSelect";

const MAX_EVENT_TAGS = 5;

const EventSummarySection = ({
  data,
  isLoading,
  setModalType,
  permissions = [],
  modalType,
  onUpdateEvent,
  refetchEvent,
}: SectionProps<EventDetailSummarySection["data"]>) => {
  const navigate = useNavigate();
  const { onCopy } = useClipboard(data?.preview_url);
  const { onCopy: _onCopyId } = useClipboard(data?.id);
  const {
    state: { hasScheduledUpdatesForField, getScheduledUpdateForField },
  } = useScheduledUpdates();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [progress, updateProgress] = useProgress({
    poster: false,
  });
  const dialog = useDialog();
  const toast = useToast();
  const [loading, updateLoading] = useLoading({
    event: false,
    blocking: false,
    duplicate: false,
    tags: false,
  });
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [eventTags, setEventTags] = useState<string[]>([]);
  const [organizationTagOptions, setOrganizationTagOptions] = useState<
    Option[]
  >([]);
  const hasVenue = !!data.venue;

  useEffect(() => {
    setEventTags(Array.isArray(data?.tags) ? data.tags : []);
  }, [data?.id, data?.tags]);

  useEffect(() => {
    const fetchOrganizationTags = async () => {
      try {
        const tags = await actions.ADMIN.fetchOrganizationTags();
        setOrganizationTagOptions(
          tags.map((tag) => ({ label: tag, value: tag.trim() }))
        );
      } catch (err) {
        console.error("Unable to fetch organization tags", err);
      }
    };

    fetchOrganizationTags();
  }, []);

  const hasTooManyEventTags = eventTags.length > MAX_EVENT_TAGS;
  const originalEventTags = Array.isArray(data?.tags) ? data.tags : [];
  const hasEventTagChanges =
    JSON.stringify(eventTags) !== JSON.stringify(originalEventTags);
  const eventTagValues: Option[] = eventTags.map((tag) => ({
    label: tag,
    value: tag,
  }));

  const normalizeTagOptions = useCallback((options: Option[]) => {
    const seen = new Set<string>();
    const normalized: string[] = [];
    for (const option of options) {
      const value = option.value?.trim();
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      normalized.push(value);
    }
    return normalized;
  }, []);

  const onCopyId = useCallback(() => {
    _onCopyId();
    toast({
      title: "Event ID copied to clipboard",
      status: "success",
      duration: 1000,
      isClosable: true,
    });
  }, [toast]);

  const onDuplicate = useCallback(
    ({ start_at }: { start_at: string }) => {
      return actions.ADMIN.duplicateEvent(data.id, { start_at });
    },
    [data]
  );
  const { open: openScheduledUpdates } = useContext(ScheduledUpdatesContext);
  const {
    isOpen: isUpcomingScheduledUpdateDialogOpen,
    open: openUpcomingScheduledUpdateDialog,
    close: closeUpcomingScheduledUpdateDialog,
  } = useContext(UpcomingScheduledUpdateDialogContext);

  const handleTagsSelectionChange = useCallback(
    (values: Option[]) => {
      const normalizedTags = normalizeTagOptions(values);
      setEventTags(normalizedTags);
    },
    [normalizeTagOptions]
  );

  const handleCloseTagsModal = useCallback(() => {
    setEventTags(Array.isArray(data?.tags) ? data.tags : []);
    setIsTagsModalOpen(false);
  }, [data?.tags]);

  const handleSaveEventTags = useCallback(async () => {
    updateLoading("tags", true);
    try {
      await actions.ADMIN.updateEventSync(data.id, "tags", eventTags);
      toast({
        title: "Event tags saved",
        description: "Event tags were updated.",
        status: "success",
      });
      if (refetchEvent) {
        await refetchEvent({ silent: true });
      }
      setIsTagsModalOpen(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      updateLoading("tags", false);
    }
  }, [data.id, eventTags, refetchEvent, toast, updateLoading]);

  return (
    <>
      <UpcomingScheduledUpdateDialog
        isLoading={isLoading}
        onEditScheduledUpdate={() => {
          console.log("onEditScheduledUpdate");
          const scheduledUpdate = getScheduledUpdateForField(
            data.scheduled_updates,
            "name"
          );

          closeUpcomingScheduledUpdateDialog();
          if (scheduledUpdate) {
            openScheduledUpdates({ scheduledUpdate, field: "name" });
          }
        }}
      />
      <EditVenueModal
        isLoading={loading.event || isLoading}
        isOpen={modalType === EditModalType.VENUE}
        data={data}
        onClose={() => setModalType(EditModalType.INACTIVE)}
        onSave={async (value) => {
          try {
            await onUpdateEvent("venue", JSON.stringify(value));
          } finally {
            updateLoading("event", false);
          }
        }}
      />
      <ShowIfHasPermission permissions={permissions} checkFor="write">
        <DuplicateEventModal
          data={{
            name: data.name,
            start_at: data.start_at,
            timezone: data.timezone,
          }}
          isOpen={modalType === EditModalType.DUPLICATE}
          isLoading={loading.event || loading.duplicate}
          onClose={() => setModalType(EditModalType.INACTIVE)}
          onSave={async (value) => {
            updateLoading("duplicate", true);

            try {
              const duplicated = await onDuplicate(value);

              toast({
                title: `${data.name} - event duplicated`,
                description: `You're now seeing "[Copy] ${data.name}" which now in DRAFT state.`,
                status: "success",
              });

              navigate(`/events/${duplicated.event_id}`);
            } catch (e) {
              console.log(e);
              toast({
                title: "Error duplicating event",
                description: `Was not possible to duplicate event, please contact support`,
                status: "error",
              });
            } finally {
              setModalType(EditModalType.INACTIVE);
              updateLoading("duplicate", false);
            }
          }}
        />
      </ShowIfHasPermission>
      <EditNameModal
        data={data}
        isOpen={modalType === EditModalType.NAME}
        isLoading={loading.event || isLoading}
        onClose={() => setModalType(EditModalType.INACTIVE)}
        onSave={async (value) => {
          try {
            if ("name" in value) {
              await onUpdateEvent("name", value.name);
            }

            if ("subtitle" in value) {
              await onUpdateEvent("subtitle", value.subtitle);
            }

            if ("age_restriction" in value) {
              await onUpdateEvent("age_restriction", value.age_restriction);
            }

            const scheduledUpdate = getScheduledUpdateForField(
              data.scheduled_updates,
              "name"
            );

            if (scheduledUpdate) {
              openUpcomingScheduledUpdateDialog();
            }
          } finally {
            updateLoading("event", false);
          }
        }}
      />
      <Modal
        size="xl"
        isOpen={isTagsModalOpen}
        onClose={handleCloseTagsModal}
        closeOnOverlayClick={!loading.tags}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Stack pt={4} spacing={3}>
              <Stack spacing={0}>
                {" "}
                <Text fontSize="xl" fontWeight="bold">
                  Event Tags
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Tags are automatically applied to all contacts that purchase
                  tickets for this event.
                </Text>
              </Stack>

              <KYDMultiSelect
                id="event-tags"
                label=""
                placeholder="Search or select multiple tags"
                options={organizationTagOptions}
                values={eventTagValues}
                onChange={handleTagsSelectionChange}
                createable
                isDisabled={loading.tags}
              />
              {hasTooManyEventTags ? (
                <HStack>
                  <FiInfo size={14} color="#000000" />
                  <Text fontSize="sm">
                    No more than 5 tags can be added to an event
                  </Text>
                </HStack>
              ) : null}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              variant="ghost"
              onClick={handleCloseTagsModal}
              isDisabled={loading.tags}
            >
              Cancel
            </Button>
            <Button
              color="white"
              bg="green.500"
              isLoading={loading.tags}
              isDisabled={!hasEventTagChanges || hasTooManyEventTags}
              onClick={handleSaveEventTags}
            >
              Save Tags
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Stack direction={["column", "row"]}>
        <Stack w="100%" direction={{ base: "column", md: "row" }} spacing={3}>
          <VStack>
            <Image
              fit={"contain"}
              rounded={"lg"}
              maxH="150px"
              maxW={"150px"}
              src={data.image}
              fallbackSrc="https://content.kydlabs.com/system/plcaeholderimage.png"
            />
            <ShowIfHasPermission permissions={permissions} checkFor="write">
              <Button
                isDisabled={isLoading || loading.event}
                isLoading={isLoading || loading.event}
                onClick={() => {
                  if (uploadRef && uploadRef.current) {
                    uploadRef.current.click();
                  }
                }}
                aria-label="Upload event image"
                variant={"outline"}
                leftIcon={<FaCloudUploadAlt />}
              >
                Change Poster
              </Button>
            </ShowIfHasPermission>
            <ShowIfHasPermission permissions={permissions} checkFor="write">
              <>
                {progress.poster > 0 && (
                  <Progress
                    h="15px"
                    rounded={"lg"}
                    colorScheme={"green"}
                    value={progress.poster}
                  />
                )}
                {!progress.poster || progress.poster === 0 ? (
                  <Box h="15px" w="100%" />
                ) : null}
                <ReactS3Uploader
                  style={{ display: "none" }}
                  //@ts-ignore
                  inputRef={uploadRef}
                  getSignedUrl={(file, callback) =>
                    actions.ADMIN.createUploadUrl(file, callback)
                  }
                  accept="image/*"
                  onProgress={(p) => {
                    updateProgress("poster", p);
                  }}
                  onError={(e) => {
                    updateLoading("event", false);
                    updateProgress("poster", 0);
                    dialog({ text: e });
                  }}
                  preprocess={(f, next) => {
                    updateLoading("event", true);
                    next(f);
                  }}
                  onFinish={async (result) => {
                    try {
                      await onUpdateEvent("poster", result.fileKey);
                    } finally {
                      updateProgress("poster", 0);
                      updateLoading("event", false);
                    }
                  }}
                  uploadRequestHeaders={(file) => ({
                    "Content-Type": file.type,
                    "x-amz-acl": "private",
                  })}
                  contentDisposition="auto"
                />
              </>
            </ShowIfHasPermission>
          </VStack>
          <Stack w="100%" spacing={4}>
            <Stack
              w="100%"
              justify={"space-between"}
              direction={{ base: "column", md: "row" }}
            >
              <Stack spacing={0} justifyContent={"center"}>
                <HStack
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                >
                  <Stack
                    direction={["column", "row"]}
                    alignItems={["flex-start", "center"]}
                    alignSelf={"flex-start"}
                    gap={2}
                    pb={[2, 0]}
                  >
                    <Stack spacing={0}>
                      <EventDetailTitle>
                        <Text
                          w="full"
                          fontSize={"xl"}
                          onClick={onCopyId}
                          cursor="pointer"
                        >
                          {data.name}
                        </Text>
                      </EventDetailTitle>

                      {data.subtitle ? (
                        <Text w="full">{data.subtitle}</Text>
                      ) : null}
                      <HStack pt={1} spacing={2} wrap="wrap">
                        {eventTags.length > 0 ? (
                          eventTags.map((tag) => (
                            <Tag
                              key={tag}
                              size="sm"
                              borderRadius="sm"
                              variant="subtle"
                            >
                              <TagLabel>{tag}</TagLabel>
                            </Tag>
                          ))
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            No event tags
                          </Text>
                        )}
                        <ShowIfHasPermission
                          permissions={permissions}
                          checkFor="write"
                        >
                          <Text
                            fontSize="xs"
                            onClick={() => setIsTagsModalOpen(true)}
                            color="#1D74FF"
                            textDecor={"underline"}
                            cursor={"pointer"}
                          >
                            {eventTags.length ? "Edit tags" : "Add tags"}
                          </Text>
                        </ShowIfHasPermission>
                      </HStack>
                    </Stack>

                    <ScheduledUpdatesBadge
                      scheduledUpdates={data.scheduled_updates}
                      field="name"
                      hasScheduledUpdatesForField={hasScheduledUpdatesForField}
                    />
                  </Stack>
                  <ShowIfHasPermission
                    permissions={permissions}
                    checkFor="write"
                  >
                    <IconButton
                      isDisabled={isLoading || loading["blocking"]}
                      onClick={() => setModalType(EditModalType.NAME)}
                      icon={<FiEdit2 fontSize="1.25rem" />}
                      variant="ghost"
                      aria-label="Edit member"
                    />
                  </ShowIfHasPermission>
                </HStack>
                <Divider my={2} />

                {hasVenue ? (
                  <VenueDisplayHeader
                    venue={data.venue}
                    permissions={permissions}
                    setModalType={setModalType}
                  />
                ) : (
                  <ShowIfHasPermission
                    permissions={permissions}
                    checkFor="write"
                  >
                    <Button
                      maxW={"180px"}
                      isDisabled={isLoading || loading.event}
                      isLoading={loading.event}
                      onClick={() => setModalType(EditModalType.VENUE)}
                      aria-label="Change venue"
                      variant={"outline"}
                      leftIcon={<FaWarehouse />}
                    >
                      {hasVenue ? "Change Venue" : "Set Venue"}
                    </Button>
                  </ShowIfHasPermission>
                )}

                <Stack spacing={0}>
                  <HStack alignItems={"center"} gap={1}>
                    <FiCalendar size={14} color="#4A5568" />
                    {data.display_start_at ? (
                      <Text fontSize={"sm"} fontWeight={"medium"}>
                        {data.display_start_at}
                      </Text>
                    ) : null}
                    <Text> - </Text>
                    {data.display_end_at ? (
                      <Text fontSize={"sm"} fontWeight={"medium"}>
                        {data.display_end_at}
                      </Text>
                    ) : null}
                  </HStack>

                  {data.is_external ? (
                    <Badge colorScheme={"blue"}>External</Badge>
                  ) : null}
                </Stack>
              </Stack>

              <Stack>
                {data.preview_url ? (
                  <HStack>
                    <ShowIfHasPermission
                      permissions={permissions}
                      checkFor="write"
                    >
                      <>
                        <Button
                          variant={"outline"}
                          onClick={() =>
                            window.open(data.preview_url, "_blank")
                          }
                          rightIcon={<FiExternalLink />}
                        >
                          Preview Event
                        </Button>
                        <IconButton
                          variant={"outline"}
                          onClick={() => {
                            onCopy();
                            showSuccessToast(toast, "Preview Link Copied");
                          }}
                          icon={<FiCopy />}
                          aria-label="Copy preview link"
                        />
                      </>
                    </ShowIfHasPermission>
                  </HStack>
                ) : null}
                {data.short_link ? (
                  <Button
                    variant={"outline"}
                    onClick={() => window.open(data.short_link, "_blank")}
                    rightIcon={<FiExternalLink />}
                  >
                    Go To Event
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <Menu>
            <MenuButton
              as={IconButton}
              variant={"outline"}
              icon={<IoEllipsisVertical />}
            />
            <MenuList>
              <MenuItem onClick={() => setModalType(EditModalType.DUPLICATE)}>
                Duplicate
              </MenuItem>
            </MenuList>
          </Menu>
        </ShowIfHasPermission>
      </Stack>

      <Divider />
    </>
  );
};

export default withScheduledUpdates(
  withUpcomingScheduledUpdateDialog(EventSummarySection)
);
